<script setup lang="ts">
import {computed, onUnmounted, type PropType, ref, watch} from 'vue';
import { Icon } from '@iconify/vue';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import ColumnGroup from 'primevue/columngroup';
import Row from 'primevue/row';
import {formatedDate} from '@/constants'
// @ts-ignore
import html2pdf from 'html2pdf.js';
import type { BatchStockResponse } from '@/types/report-types';
import {BatchStateValue} from "@/types/batch-types";
import {getBatchById} from "@/api/batches";
import {getProductById} from "@/api/products";

const props = defineProps({
  visible: { type: Boolean, required: true },
  onClose: { type: Function as PropType<() => void>, required: true },
  reportData: { type: Object as PropType<BatchStockResponse | null>, default: null },
  selectedDate: { type: Date as PropType<Date | null>, default: null },
  selectedQuality: { type: String as PropType<string | null>, default: null },
  selectedBatchState: { type: Object as PropType<BatchStateValue | null>, default: null },
  companyName: { type: String, default: 'Techknital Fabrics Ltd' },
  companyAddress: { type: String, default: 'Unit 8 - 9, Lime Kilns Business Park\nLime Kilns Way\nHinckley\nLeicestershire, LE10 #EL' },
  companyTel: { type: String, default: '+44 (0)1455 254350' },
  companyFax: { type: String, default: '+44 (0)1455 238998' },
});

const PDF_EXPORT_ELEMENT_ID = 'batch-roll-stock-report-content';

const qualityDetailsMap = ref<Map<string, { name: string | null, isLoading: boolean }>>(new Map());
const batchDetailsMap = ref<Map<string, { batchNumber: string | null, isLoading: boolean }>>(new Map());
computed(() => {
  const qualityLoading = [...qualityDetailsMap.value.values()].some(detail => detail.isLoading);
  const batchLoading = [...batchDetailsMap.value.values()].some(detail => detail.isLoading);
  return qualityLoading || batchLoading;
});
const formattedDate = computed(() =>
    props.selectedDate ? formatedDate(props.selectedDate) : 'N/A'
);

const getBatchStateLabel = (state: BatchStateValue | null) => {
  if (state === null) return 'All States';
  switch (state) {
    case BatchStateValue.GREIG: return 'Greig';
    case BatchStateValue.OFFSITE: return 'Offsite';
    case BatchStateValue.FINISHED: return 'Finished';
    case BatchStateValue.AVAILABLE: return 'Available';
    case BatchStateValue.DISPATCHED: return 'Dispatched';
    default: return 'Unknown';
  }
};

const fetchQualityNames = async () => {
  if (!props.reportData?.rolls) {
    qualityDetailsMap.value.clear();
    return;
  }

  const mapCopy = new Map(qualityDetailsMap.value);
  const promises: Promise<void>[] = [];

  const qualityIds = new Set<string>();
  props.reportData.rolls.forEach(roll => {
    if (roll.knittingQualityId) qualityIds.add(roll.knittingQualityId);
    if (roll.finishingQualityId) qualityIds.add(roll.finishingQualityId);
  });

  for (const qualityId of qualityIds) {
    if (!mapCopy.has(qualityId) || mapCopy.get(qualityId)?.name === null) {
      mapCopy.set(qualityId, { name: null, isLoading: true });

      promises.push(
          getProductById(qualityId)
              .then(product => {
                mapCopy.set(qualityId, {
                  name: product.finishedReference || product.unfinishedReference || "Unknown Quality",
                  isLoading: false
                });
              })
              .catch(() => {
                mapCopy.set(qualityId, {
                  name: "Quality N/A",
                  isLoading: false
                });
              })
      );
    }
  }

  qualityDetailsMap.value = new Map(mapCopy);
  await Promise.all(promises);
  qualityDetailsMap.value = new Map(mapCopy);
};

const fetchBatchNumbers = async () => {
  if (!props.reportData?.rolls) {
    batchDetailsMap.value.clear();
    return;
  }

  const mapCopy = new Map(batchDetailsMap.value);
  const promises: Promise<void>[] = [];

  const batchIds = new Set<string>();
  props.reportData.rolls.forEach(roll => {
    if (roll.batchId) batchIds.add(roll.batchId);
  });

  for (const batchId of batchIds) {
    if (!mapCopy.has(batchId) || mapCopy.get(batchId)?.batchNumber === null) {
      mapCopy.set(batchId, { batchNumber: null, isLoading: true });

      promises.push(
          getBatchById(batchId)
              .then(batch => {
                mapCopy.set(batchId, {
                  batchNumber: batch.batchNumber || "Unknown Batch",
                  isLoading: false
                });
              })
              .catch(() => {
                mapCopy.set(batchId, {
                  batchNumber: "Batch N/A",
                  isLoading: false
                });
              })
      );
    }
  }

  batchDetailsMap.value = new Map(mapCopy);
  await Promise.all(promises);
  batchDetailsMap.value = new Map(mapCopy);
};


watch(() => props.reportData, (newData) => {
  if (props.visible && newData) {
    qualityDetailsMap.value.clear();
    batchDetailsMap.value.clear();
    fetchQualityNames();
    fetchBatchNumbers();
  }
}, { deep: true });

watch(() => props.visible, (visible) => {
  if (visible && props.reportData) {
    if (qualityDetailsMap.value.size === 0) {
      fetchQualityNames();
    }
    if (batchDetailsMap.value.size === 0) {
      fetchBatchNumbers();
    }
  }
});

const getQualityDisplayName = (qualityId: string | null): string => {
  if (!qualityId) return 'N/A';
  const detail = qualityDetailsMap.value.get(qualityId);
  return detail?.isLoading ? 'Loading...' : (detail?.name || 'Quality N/A');
};

const getBatchDisplayName = (batchId: string | null): string => {
  if (!batchId) return 'N/A';
  const detail = batchDetailsMap.value.get(batchId);
  return detail?.isLoading ? 'Loading...' : (detail?.batchNumber || 'Batch N/A');
};

const onDownloadReportPdf = () => {
  const reportElement = document.getElementById(PDF_EXPORT_ELEMENT_ID);
  if (!reportElement || !props.reportData) return;
  const filename = `batch-roll-stock-${props.selectedDate ? new Date(props.selectedDate).toISOString().split('T')[0] : 'na'}.pdf`;
  html2pdf().set({
    margin: [10, 5, 10, 5],
    filename,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' }
  }).from(reportElement).save();
};

const hasReportData = computed(() => !!(props.reportData?.rolls?.length));

const formatCurrency = (value?: number) => {
  return typeof value === 'number' ? `£ ${value.toFixed(2)}` : '£ 0.00';
};

const formatNumberTwoDecimals = (value?: number) => typeof value === 'number' ? value.toFixed(2) : '-';

onUnmounted(() => {
  qualityDetailsMap.value.clear();
  batchDetailsMap.value.clear();
});

</script>

<template>
  <Dialog :visible="visible" modal :draggable="false" :style="{ width: '95rem', maxWidth: '98vw', minHeight: '90vh'}" :closable="false" @update:visible="onClose" class="report-dialog-custom-header">
    <template #header>
      <div class="flex justify-between items-center w-full">
        <Button link @click="onClose" aria-label="Back to report page" class="flex items-center gap-x-1 p-0 text-gray-600 hover:text-primary-default">
          <Icon icon="material-symbols:arrow-back-rounded" class="text-2xl md:text-3xl group-hover:-translate-x-1 transition-transform" />
          <span class="text-sm md:text-base hidden sm:inline">Back to Report Page</span>
        </Button>
        <div class="flex-grow text-center">
          <h2 class="text-lg md:text-xl font-semibold">Batch Stock Report</h2>
        </div>
        <div class="flex items-center gap-x-2">
          <Button label="Download as PDF" @click="onDownloadReportPdf" :disabled="!hasReportData" class="p-button-danger text-sm md:text-base">
            <template #icon><Icon icon="material-symbols:download-2" class="text-xl ml-3 order-1" /></template>
          </Button>
        </div>
      </div>
    </template>

    <div class="dialog-main-content flex-grow overflow-y-auto">
      <div v-if="!reportData && visible" class="text-center py-10 text-gray-500">
        No data to display.
      </div>
      <div v-else-if="hasReportData" :id="PDF_EXPORT_ELEMENT_ID" class="p-2 md:p-4 bg-white">
        <div class="flex justify-between items-start mb-6">
          <div>
            <img src="@/assets/images/logo.png" alt="Company Logo" class="h-12 mb-1" />
          </div>
          <div class="text-right text-xs text-gray-500 whitespace-pre-line">
            <p>{{ companyAddress }}</p>
            <p>Tel: {{ companyTel }}</p>
            <p>Fax: {{ companyFax }}</p>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-md shadow-sm">
          <div>
            <h3 class="text-md font-semibold text-gray-700">Report Details</h3>
            <p class="text-sm text-gray-600">Date: {{ formattedDate }}</p>
            <p class="text-sm text-gray-600">Quality: {{ selectedQuality || 'All Qualities' }}</p>
            <p class="text-sm text-gray-600">Batch State: {{ getBatchStateLabel(selectedBatchState) }}</p>
          </div>
          <div class="md:text-right">
            <h3 class="text-md font-semibold text-gray-700">Summary</h3>
            <p class="text-sm text-gray-600">Total Rolls: {{ reportData?.totalRollCount || 0 }}</p>
            <p class="text-sm text-gray-600">Total Weight: {{ formatNumberTwoDecimals(reportData?.totalWeight) }} kg</p>
            <p class="text-sm text-gray-600">Total Length: {{ formatNumberTwoDecimals(reportData?.totalLength) }} m</p>
          </div>
        </div>

        <div class="overflow-x-auto">
          <DataTable
              :value="reportData?.rolls"
              responsiveLayout="scroll"
              class="p-datatable-sm batch-roll-stock-table min-w-full"
              stripedRows
              :rowHover="true"
              :scrollable="true"
              scrollHeight="60vh"
          >
            <Column field="rollNumber" header="Roll Number" sortable :style="{ minWidth: '80px', whiteSpace: 'nowrap' }"></Column>
            <Column field="batchId" header="Batch Number" sortable :style="{ minWidth: '100px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ getBatchDisplayName(slotProps.data.batchId) }}</template>
            </Column>
            <Column field="length" header="Length" sortable :style="{ minWidth: '100px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ formatNumberTwoDecimals(slotProps.data.length) }} {{ slotProps.data.lengthUnit }}</template>
            </Column>
            <Column field="weight" header="Weight" sortable :style="{ minWidth: '100px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ formatNumberTwoDecimals(slotProps.data.weight) }} kg</template>
            </Column>
            <Column field="productionCost" header="Production Cost" sortable :style="{ minWidth: '100px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ formatCurrency(slotProps.data.productionCost) }}</template>
            </Column>
            <Column field="knittingQualityId" header="Knitting Quality" sortable :style="{ minWidth: '100px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ getQualityDisplayName(slotProps.data.knittingQualityId) }}</template>
            </Column>
            <Column field="finishingQualityId" header="Finishing Quality" sortable :style="{ minWidth: '100px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ getQualityDisplayName(slotProps.data.finishingQualityId) }}</template>
            </Column>

            <ColumnGroup type="footer" v-if="reportData && reportData.rolls.length > 0">
              <Row>
                <Column footer="Totals:" :colspan="2" footerStyle="text-align:left; font-weight: bold; color: #1f2937; padding-left: 0.5rem;" />
                <Column :footer="`${formatNumberTwoDecimals(reportData.totalLength)} m`" footerStyle="text-align:right; font-weight: bold; color: #1f2937;" />
                <Column :footer="`${formatNumberTwoDecimals(reportData.totalWeight)} kg`" footerStyle="text-align:right; font-weight: bold; color: #1f2937;" />
                <Column :footer="formatCurrency(reportData.totalProductionCost)" footerStyle="text-align:right; font-weight: bold; color: #1f2937;" />
                <Column footer="" :colspan="2" footerStyle="text-align:right; font-weight: bold; color: #1f2937;" />
              </Row>
            </ColumnGroup>
          </DataTable>
        </div>

      </div>
      <div v-else-if="!hasReportData && visible" class="text-center py-10 text-gray-500">
        No batch stock data found for the selected criteria.
      </div>
    </div>
  </Dialog>
</template>

<style scoped></style>